import { MutableRefObject, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { TimerDurations } from "@lifeat/shared/widget/PomodoroTimerTypes";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { useTimerVolumeContext } from "@contexts/TimerVolumeContext";
import { HostPomodoroService } from "../../../service/HostPomodoroService";
import { PomodoroAnalyticsService } from "../../../service/PomodoroAnalyticsService";
import Pomodoro from "./Pomodoro";
import { STOP_TIMER_LABEL } from "./TimerWidgetConstants";
import { useAlarmSoundOptions } from "./hooks/useAlarmSoundOptions";
import { useServerPomodoro } from "./hooks/useServerPomodoro";
import useTimerActions from "./hooks/useTimerActions";

interface HostPomodoroProps {
    hidden: boolean;
    updateCompletedPomos: (numComplete: number) => void;
    updateHidePomodoroCount: (value: boolean) => void;
}
export default function HostPomodoro(props: HostPomodoroProps) {
    // Note: if the timerVolume (in saved user data) is set to 0, timerAudioVol will default to DEFAULT_TIMER_VOLUME
    const timerAudioRef: MutableRefObject<HTMLAudioElement | null> = useRef(null);

    const { roomId } = useParams<{ roomId: string }>();

    const { groupTimerVolume, changeTimerVolume } = useTimerVolumeContext();

    const amplitudeService = AmplitudeService.getInstance();
    const analyticsService = PomodoroAnalyticsService(amplitudeService);
    const pomodoroService = HostPomodoroService(roomId, analyticsService);
    const alarmSoundOptions = useAlarmSoundOptions(pomodoroService);

    const {
        lastEvent,
        timerDisplayText,
        pomodoroSettingsInput,
        dispatchPomodoroSettingsInput,
        duration,
        saveSettings,
    } = useServerPomodoro({
        roomId,
        hidden: props.hidden,
        alarmSoundOptions,
        pomodoroService,
        audioRef: timerAudioRef,
        onPomodoroCountChange: props.updateCompletedPomos,
    });

    const timerDurations: TimerDurations = useMemo(() => {
        return {
            pomodoro: duration("pomodoro"),
            shortBreak: duration("short_break"),
            longBreak: duration("long_break"),
        };
    }, [duration]);

    const [toggleTimer, resetTimer, isServerActionLoading] = useTimerActions(
        roomId,
        pomodoroService,
        timerDurations,
        lastEvent,
    );

    return (
        <Pomodoro
            hidden={props.hidden}
            stopTimerActionButtonLabel={STOP_TIMER_LABEL}
            canEditSettings={false}
            isRunning={lastEvent.isRunning ?? false}
            timerDisplayText={timerDisplayText}
            alarmSoundOptions={alarmSoundOptions}
            pomodoroService={pomodoroService}
            pomodoroSettingsInput={pomodoroSettingsInput}
            dispatchPomodoroSettingsInput={dispatchPomodoroSettingsInput}
            timerAudioRef={timerAudioRef}
            timerVolume={groupTimerVolume}
            setTimerVolume={(newVol: number) => changeTimerVolume("group", newVol)}
            onTimerToggle={toggleTimer}
            onTimerReset={resetTimer}
            onSettingsSave={saveSettings}
            isServerActionLoading={isServerActionLoading}
            allowAutotransitionToggle={false}
        />
    );
}
